import { useEffect, useState, useRef } from "react";
import { isPrerendering } from "@/lib/prerender_flag";

const COUNTRIES = [
  { code: "US", flag: "🇺🇸", weight: 30 },
  { code: "IN", flag: "🇮🇳", weight: 22 },
  { code: "GB", flag: "🇬🇧", weight: 14 },
  { code: "DE", flag: "🇩🇪", weight: 11 },
  { code: "FR", flag: "🇫🇷", weight: 9 },
  { code: "BR", flag: "🇧🇷", weight: 8 },
  { code: "JP", flag: "🇯🇵", weight: 7 },
  { code: "CA", flag: "🇨🇦", weight: 7 },
  { code: "AU", flag: "🇦🇺", weight: 6 },
  { code: "NL", flag: "🇳🇱", weight: 5 },
  { code: "ES", flag: "🇪🇸", weight: 4 },
  { code: "IT", flag: "🇮🇹", weight: 4 },
  { code: "MX", flag: "🇲🇽", weight: 3 },
  { code: "SG", flag: "🇸🇬", weight: 3 },
  { code: "ID", flag: "🇮🇩", weight: 3 },
];

export default function FlagCounterPreview({ cfg, dataOverride = null }) {
  const [bump, setBump] = useState(0);
  const [pulseCC, setPulseCC] = useState(() => new Set());
  const prevCounts = useRef({});

  useEffect(() => {
    if (isPrerendering()) return undefined;
    const t = setInterval(() => setBump((b) => b + 1), 2400);
    return () => clearInterval(t);
  }, []);

  // v60.102.9 — jitter must be DETERMINISTIC. `Math.random()` here ran during
  // render, so the prerendered counts never matched the client's first paint
  // and React threw hydration error #418 on every page carrying a flag
  // counter. A hash of the country code plus the tick gives the same restless
  // movement with none of the mismatch; bump=0 (first paint) is stable.
  const jitter = (code, tick) => {
    let h = 7;
    for (let i = 0; i < code.length; i += 1) h = (h * 31 + code.charCodeAt(i)) % 101;
    return (h + tick * 17) % 9;
  };

  const data =
    dataOverride ||
    COUNTRIES.map((c) => ({
      ...c,
      count: Math.max(1, Math.round(c.weight * 10 + (bump % 3) * c.weight + jitter(c.code, bump))),
    })).sort((a, b) => b.count - a.count);

  // Detect which country counts grew vs. previous tick, pulse those for 5s.
  useEffect(() => {
    const grew = [];
    data.forEach((d) => {
      const prev = prevCounts.current[d.code] || 0;
      if (d.count > prev && prev > 0) grew.push(d.code);
      prevCounts.current[d.code] = d.count;
    });
    if (grew.length) {
      setPulseCC((s) => {
        const next = new Set(s);
        grew.forEach((cc) => next.add(cc));
        return next;
      });
      setTimeout(() => {
        setPulseCC((s) => {
          const next = new Set(s);
          grew.forEach((cc) => next.delete(cc));
          return next;
        });
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  const items = data.slice(0, (cfg.rows || 10) * (cfg.cols || 3));
  const total = data.reduce((a, b) => a + b.count, 0);

  return (
    <div
      data-testid="preview-flag-counter"
      style={{
        width: cfg.width,
        maxWidth: "100%",
        background: cfg.bg_color,
        color: cfg.text_color,
        border: `1px solid ${cfg.border_color}`,
        fontFamily: `${cfg.font}, system-ui, sans-serif`,
        fontSize: 12,
        // v60.69 — CSS containment isolates this widget's layout from
        // the surrounding document, killing the document-wide reflow
        // that fired on every flag pulse.
        contain: "layout style paint",
      }}
    >
      {/* v60.69 — iteration count 10 → 3. Same visibility, 70% less work. */}
      <style>{`@keyframes ltf-flag-new{0%,50%{background-color:#38bdf8;}100%{background-color:transparent;}}.ltf-flag-new{animation:ltf-flag-new 1s ease-out 3;}`}</style>
      {(() => null)()}
      <div
        style={{
          background: cfg.header_bg,
          color: cfg.header_fg,
          padding: "8px 10px",
          fontWeight: 700,
          textTransform: "uppercase",
          fontSize: 11,
          letterSpacing: ".05em",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{({ es: "Contador de Banderas", fr: "Compteur de Drapeaux", de: "Flaggenzähler", hi: "फ़्लैग काउंटर", pt: "Contador de Bandeiras", it: "Contatore Bandiere", ru: "Счётчик Флагов", ja: "国旗カウンター", zh: "国旗计数器", ar: "عداد الأعلام", nl: "Vlaggenteller" }[cfg.lang]) || "Flag Counter"}</span>
        <span className="flex items-center gap-2 font-mono normal-case tracking-normal text-[11px] font-semibold">
          <span>{({ es: "Hoy", fr: "Aujourd'hui", de: "Heute", hi: "आज", pt: "Hoje", it: "Oggi", ru: "Сегодня", ja: "今日", zh: "今天", ar: "اليوم", nl: "Vandaag" }[cfg.lang]) || "Today"} <b className="ml-0.5">{Math.max(1, Math.round(total * 0.18))}</b></span>
          <span className="opacity-50">·</span>
          <span>{({ es: "Total", fr: "Total", de: "Gesamt", hi: "कुल", pt: "Total", it: "Totale", ru: "Всего", ja: "合計", zh: "总计", ar: "الكل", nl: "Totaal" }[cfg.lang]) || "All"} <b className="ml-0.5">{total.toLocaleString()}</b></span>
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cfg.cols || 3}, 1fr)`,
          gap: 1,
          background: cfg.border_color,
        }}
      >
        {items.map((x) => (
          <div
            key={x.code}
            className={pulseCC.has(x.code) ? "ltf-flag-new" : ""}
            style={{
              background: cfg.bg_color,
              padding: "8px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {cfg.show_flag !== false && <span style={{ fontSize: 18, lineHeight: 1 }}>{x.flag}</span>}
            {cfg.show_country_code !== false && (
              <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, opacity: 0.7 }}>{x.code}</span>
            )}
            <span style={{ marginLeft: "auto", fontWeight: 700 }}>{x.count}</span>
          </div>
        ))}
      </div>
      <BadgePreview style={cfg.badge_style} borderColor={cfg.border_color} />
    </div>
  );
}

function BadgePreview({ style, borderColor }) {
  const s = (style || "minimal").toLowerCase();
  if (s === "pill") {
    return (
      <div style={{ padding: "8px 10px", borderTop: `1px solid ${borderColor}`, display: "flex", justifyContent: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0A0A0A", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00E676", display: "inline-block" }} />
          FeedPulse
        </span>
      </div>
    );
  }
  if (s === "animated") {
    return (
      <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: 0.85 }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00E676", display: "inline-block", animation: "fpBadgePulseF 1.6s ease-in-out infinite" }} />
        <span style={{ opacity: 0.75 }}>Powered by FeedPulse</span>
        <style>{`@keyframes fpBadgePulseF{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>
    );
  }
  if (s === "icon") {
    return (
      <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, opacity: 0.7, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, background: "#002FA7", color: "#fff", fontWeight: 900, fontSize: 9 }}>F</span>
        <span>FeedPulse</span>
      </div>
    );
  }
  return (
    <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, opacity: 0.7, textAlign: "center" }}>
      Flag counter · FeedPulse
    </div>
  );
}
